import { useLocation, useNavigate } from "react-router-dom";

import { persistLocale } from "@/i18n/detect-locale";
import { useLocale } from "@/i18n/locale-context";
import { type LocaleCode,LOCALES } from "@/i18n/types";
import { cn } from "@/lib/utils";

const LOCALE_LABELS: Record<LocaleCode, string> = {
  en: "EN",
  es: "ES",
};

interface LocaleSwitcherProps {
  className?: string;
}

export function LocaleSwitcher({ className }: LocaleSwitcherProps): JSX.Element {
  const { locale, t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const switchTo = (target: LocaleCode): void => {
    if (target === locale) return;
    const parts = location.pathname.split("/").filter(Boolean);
    const rest = parts.slice(1).join("/");
    const nextPath = rest ? `/${target}/${rest}` : `/${target}`;
    persistLocale(target);
    void navigate(nextPath + location.search + location.hash, { replace: false });
  };

  return (
    <div
      aria-label={t("actions.switchLanguage")}
      className={cn(
        "inline-flex items-center gap-1 rounded-full-pill border border-spotify-border bg-spotify-surface/70 p-1 text-xs font-bold",
        className,
      )}
      role="group"
    >
      {LOCALES.map((item) => {
        const isActive = item === locale;
        return (
          <button
            aria-pressed={isActive}
            className={cn(
              "rounded-full-pill px-3 py-1 transition",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            key={item}
            onClick={() => {
              switchTo(item);
            }}
            type="button"
          >
            {LOCALE_LABELS[item]}
          </button>
        );
      })}
    </div>
  );
}
